import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  OnInit,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
  viewChild,
  viewChildren,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { A11yModule } from '@angular/cdk/a11y';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError, concatMap, EMPTY, finalize, groupBy, mergeMap, Observable, of, Subject, tap } from 'rxjs';
import { TestModuleService } from '../../../services/test-module.service';
import { TestModuleStore } from '../../../services/test-module-store';
import { ModulePresenceService, PresentUser } from '../../../services/module-presence.service';
import { AccountService } from '../../../account/account.service';
import { ProjectMemberService } from '../../../services/project-member.service';
import { AvatarComponent } from '../../../components/avatar/avatar.component';
import { ProjectMemberDTO } from '../../../models/project-member.model';
import { reviewerOptionsFromMembers } from '../../../shared/project-member-options';
import { PersonOption, PersonSelectComponent } from '../../../components/person-select/person-select.component';
import { TestModule, TestModuleDetail } from '../../../models/test-module.model';
import { TestStep } from '../../../models/test-case.model';
import { toSaveFullModuleRequest } from '../module-form/module-form.mappers';
import {
  TEST_STEP_STATUSES,
  TestStepStatus,
  fromRunStatus,
  toRunStatus,
} from '../../test-case/test-step-table/test-step-status.util';
import {
  createLocalModuleRefStep,
  createLocalStep,
  isLocalId,
  nextLocalId,
} from '../../test-case/test-step-table/test-step-local.util';
import { FIELD_LENGTHS } from '../../../shared/field-lengths';
import { MultiUseConfirmDialogComponent } from '../multi-use-confirm-dialog/multi-use-confirm-dialog.component';
import { ModuleEmbedInlineComponent } from '../module-embed-inline/module-embed-inline.component';
import { Testdatenblatt } from '../../../models/testdatenblatt.model';
import { TestdatenblattService } from '../../../services/testdatenblatt.service';
import { TdbOverride, bedingungFragment, formatTcdlPreview } from '../../../shared/tcdl-ops';
import { StepConditionListComponent } from '../../../components/step-condition-list/step-condition-list.component';
import {
  TestdatenBlockComponent,
  TestdatenBlockRow,
  toTestdatenblattSummary,
} from '../../../components/testdaten-block/testdaten-block.component';
import { TdbOverrideDelta } from '../../../shared/tdb-override-delta';
import {
  activeRowCount,
  filterEntriesFor,
  formatFilterExpression,
  StepCondition,
  fromStepCondition,
  fromStepConditions,
  missingConditionFields,
  nextStepCondition,
  parseFilterExpression,
  stepConditionsOf,
  toStepConditions,
} from '../../../shared/data-sheet-filters.codec';
import { ObjectMappingService } from '../../../services/object-mapping.service';
import { ConditionLibrary } from '../../../models/object-mapping.model';

/** Ziel einer Verwerfen-Rückfrage: Drawer schließen oder eine Stack-Ebene zurück. */
type DiscardTarget = 'close' | 'back';

/** Auf den Stack gelegter Bearbeitungs-Kontext beim Öffnen eines Sub-Moduls. */
interface StackEntry {
  moduleId: string;
  title: string;
  description: string;
  authorUserId: string;
  steps: TestStep[];
  meta: TestModule | null;
  signature: string;
  /** Aufruf, dessen Testdaten die Ebene bearbeitet hat; nur die unterste Ebene hat einen. */
  contextStep: TestStep | null;
  /** Offener und zuletzt übernommener Testdaten-Entwurf dieser Ebene; `back` stellt beide wieder her. */
  tdb: { current: TdbDraftState; applied: TdbDraftState };
}

interface TdbDraftState {
  draft: TdbOverride;
  sheetId: string | null;
  condition: StepCondition | null;
}

const EMPTY_TDB: TdbDraftState = { draft: {}, sheetId: null, condition: null };

/** Die Bedingung haengt am Aufruf, nicht am Blatt; sie bleibt stehen. */
function withoutSheet(state: TdbDraftState): TdbDraftState {
  return { draft: withoutFilter(state.draft), sheetId: null, condition: state.condition };
}

interface SheetLinkChange {
  seq: number;
  sheetId: string;
  moduleId: string;
  link: boolean;
  wasKnown: boolean;
  settled: boolean;
  onSuccess?: () => void;
}

const SAVE_ERROR_GENERIC = 'modules.drawer.saveError';

/** Der Servertext nennt Modul-IDs; angezeigt wird nur die Art des Fehlers (NEX-2259). */
function saveErrorKeyFor(error: unknown): string {
  if (!(error instanceof HttpErrorResponse)) return SAVE_ERROR_GENERIC;
  if (error.status === 403) return 'modules.drawer.saveErrorForbidden';
  if (error.status === 409) return 'modules.drawer.saveErrorStale';
  const text = (error.error as { error?: unknown } | null)?.error;
  if (error.status === 400 && typeof text === 'string' && text.startsWith('Zyklische Einbindung')) {
    return 'modules.form.errors.embedCycle';
  }
  return SAVE_ERROR_GENERIC;
}

function withModuleLink(testModuleIds: string[] | undefined, moduleId: string, link: boolean): string[] {
  const others = (testModuleIds ?? []).filter((id) => id !== moduleId);
  return link ? [...others, moduleId] : others;
}

/**
 * Right-anchored Drawer zum Bearbeiten eines Testmoduls von jeder aufrufenden Stelle
 * (NEX-687). Öffnet, sobald {@link moduleId} gesetzt wird; lädt das Modul inkl. Ablauf
 * (Schritte + eingebundene Module als MODULE_REF-Zeilen, NEX-644) und persistiert über
 * den Full-Save-Endpunkt. Ein Modul-Stack erlaubt das Öffnen eines Sub-Moduls im selben
 * Drawer inkl. Zurück-Navigation in den Aufrufer-Kontext.
 */
@Component({
  selector: 'app-module-edit-drawer',
  imports: [
    A11yModule,
    ReactiveFormsModule,
    TranslatePipe,
    MatIcon,
    MatProgressSpinner,
    MultiUseConfirmDialogComponent,
    ModuleEmbedInlineComponent,
    AvatarComponent,
    PersonSelectComponent,
    StepConditionListComponent,
    TestdatenBlockComponent,
  ],
  templateUrl: './module-edit-drawer.component.html',
  styleUrl: './module-edit-drawer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModuleEditDrawerComponent implements OnInit {
  private readonly moduleService = inject(TestModuleService);
  private readonly moduleStore = inject(TestModuleStore);
  private readonly presenceService = inject(ModulePresenceService);
  private readonly accountService = inject(AccountService);
  private readonly projectMemberService = inject(ProjectMemberService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly testdatenblattService = inject(TestdatenblattService);
  private readonly injector = inject(Injector);

  readonly projectId = input.required<string>();
  /** Öffnungs-Trigger: UUID des zu bearbeitenden Moduls; {@code null} hält den Drawer geschlossen. */
  readonly moduleId = input<string | null>(null);
  /**
   * Modul, das die Seite hinter dem Drawer bearbeitet. Es darf nicht zusätzlich im Drawer
   * aufgehen: Der spätere Save der Seite würde die Drawer-Änderungen überschreiben.
   */
  readonly hostModuleId = input<string | null>(null);

  /**
   * `availableSheets` liefert die projektweiten Testdatenblaetter, die die verbundenen
   * des Moduls ableiten lassen. `contextStep` setzt nur der Modul-Editor: Die Zeile, aus
   * der er den Drawer oeffnet, bekommt auf der obersten Ebene ihren Testdaten-Block.
   * Im Testfall bleibt er leer, dort schraenkt der Modulblock die Zeilen ein (NEX-2418).
   */
  readonly availableSheets = input<readonly Testdatenblatt[]>([]);
  readonly contextStep = input<TestStep | null>(null);
  /** Halbe Eingabe der Zeile im Modul-Editor; die Seitenleiste startet damit statt mit der gespeicherten Bedingung. */
  readonly contextOverride = input<TdbOverride | null>(null);
  /** Projekt-Leser: Zeilen-Auswahl und Bedingung sind gesperrt (NEX-1084). */
  readonly readOnly = input(false);

  /** Kontext-Step der obersten Ebene; in Sub-Modulen gibt es keinen. */
  private readonly currentContextStep = signal<TestStep | null>(null);

  /** Der Aufrufer setzt {@link moduleId} zurück und gibt dem auslösenden Element den Fokus. */
  readonly closed = output<void>();
  /** Erfolgreich gespeichertes Modul (inkl. Ablauf) — Basis für den Live-Store (NEX-691). */
  readonly saved = output<TestModuleDetail>();
  /**
   * NEX-1052: „Testdaten uebernehmen" propagiert das gebufferte Override an das
   * Testfall-Formular. Der Parent patcht daraufhin den Step (siehe test-case-form).
   */
  readonly tdbOverrideApplied = output<TdbOverrideDelta>();

  /**
   * NEX-1052 Follow-up: Ausserhalb sichtbare Aenderung der Modul↔Datenblatt-N:M-
   * Relation. Der Drawer liefert seinen optimistischen Sheet-Snapshot mit, damit
   * der Parent sein `availableSheets` sofort synchron aktualisiert — kein
   * HTTP-Round-Trip-Delay bevor die Chip-Anzeige im Test-Step-Table die frisch
   * verbundene TDB findet.
   */
  readonly sheetsChanged = output<readonly Testdatenblatt[]>();

  /**
   * Ein Blatt ist vom Modul getrennt, der Server hat es bestaetigt. Aufrufe dieses Moduls
   * duerfen das Blatt nicht behalten, sonst liefe das Modul weiter je Datenzeile (NEX-1796).
   */
  readonly sheetUnlinked = output<{ moduleId: string; sheetId: string }>();

  readonly title = signal('');
  readonly description = signal('');
  readonly steps = signal<TestStep[]>([]);
  readonly meta = signal<TestModule | null>(null);
  /**
   * NEX-824: Verantwortlicher/Autor. The drawer only ever edits an existing
   * module (never creates one), so there is no current-user default here —
   * only required validation plus display/edit of the loaded value.
   */
  readonly authorControl = new FormControl<string>('', { nonNullable: true, validators: [Validators.required] });
  private readonly authorValue = toSignal(this.authorControl.valueChanges, { initialValue: '' });
  readonly users = signal<ProjectMemberDTO[]>([]);
  readonly personOptions = computed<PersonOption[]>(() => reviewerOptionsFromMembers(this.users()));

  readonly loading = signal(false);
  readonly busy = signal(false);
  readonly saveError = signal(false);
  readonly saveErrorKey = signal(SAVE_ERROR_GENERIC);

  readonly discardTarget = signal<DiscardTarget | null>(null);
  /** NEX-688: offener Multi-Use-Warndialog vor einem Save mit usageCount > 1. */
  readonly multiUseOpen = signal(false);
  private pendingSaveAction: DiscardTarget | null = null;
  /** NEX-690: aufgeklapptes Inline-Panel zum Einbinden eines Testmoduls. */
  readonly embedPanelOpen = signal(false);
  private readonly stack = signal<StackEntry[]>([]);
  readonly canGoBack = computed(() => this.stack().length > 0);

  /** Bereits eingebundene Module — im Embed-Panel nicht wählbar; das aktuelle Modul ist wählbar (NEX-2259). */
  readonly embedExcludeIds = computed(() =>
    this.steps()
      .filter((s) => s.kind === 'MODULE_REF')
      .map((s) => s.calledModuleId)
      .filter((id): id is string => !!id),
  );

  readonly statusOptions = TEST_STEP_STATUSES;
  readonly fieldLengths = FIELD_LENGTHS;

  /** ID des aktuell sichtbaren Moduls (Top-Level oder aktuelle Stack-Spitze). */
  private readonly activeModuleId = signal<string | null>(null);
  private readonly originalSignature = signal('');
  private localIdCounter = 0;
  private triggerElement: HTMLElement | null = null;

  /** NEX-692: eigener Präsenz-Eintrag + Modul, dem gerade beigetreten ist. */
  private readonly self = signal<PresentUser | null>(null);
  private joinedModuleId: string | null = null;

  /** Andere gleichzeitige Bearbeiter des aktuellen Moduls (ohne den eigenen Nutzer). */
  readonly others = computed<PresentUser[]>(() => {
    const id = this.activeModuleId();
    if (!id) {
      return [];
    }
    const me = this.self();
    return this.presenceService
      .presence(id)()
      .filter((user) => !me || user.tenantUserId !== me.tenantUserId);
  });

  readonly presenceNames = computed(() =>
    this.others()
      .map((user) => user.displayName)
      .join(', '),
  );

  readonly moduleDirty = computed(
    () =>
      this.signatureOf(this.title(), this.description(), this.authorValue(), this.steps()) !== this.originalSignature(),
  );

  readonly dirty = computed(() => this.moduleDirty() || this.tdbDirty());

  /** Nicht übernommene Testdaten-Entwürfe der Ebenen unter dem geöffneten Sub-Modul. */
  readonly outerTdbDirty = computed(() =>
    this.stack().some((entry) => tdbSignature(entry.tdb.current) !== tdbSignature(entry.tdb.applied)),
  );

  /** Ungespeicherte Modul-Änderungen der Ebenen unter dem geöffneten Sub-Modul. */
  readonly outerModuleDirty = computed(() =>
    this.stack().some(
      (entry) => this.signatureOf(entry.title, entry.description, entry.authorUserId, entry.steps) !== entry.signature,
    ),
  );

  private readonly outerDirty = computed(() => this.outerTdbDirty() || this.outerModuleDirty());

  /**
   * Aktueller Override-Draft der EINEN gerade angepassten TDB. Live gepflegt, damit
   * halb-eingegebene Bedingungen (subj gesetzt, op fehlt noch) nicht verlorengehen,
   * bevor `toStepCondition` valide wird. Beim Wechsel der aktiven TDB neu geseedet.
   */
  private readonly overrideDraft = signal<TdbOverride>({});

  /**
   * Welche TDB gerade das Override besitzt. Separat gehalten, damit der Editier-Loop
   * `currentContextStep` unberuehrt laesst und der Seed-Effect nicht laufend feuert.
   */
  private readonly activeSheetId = signal<string | null>(null);

  /**
   * Zuletzt an den Step geschriebene Bedingung. Eine halbe Eingabe ueberschreibt sie nicht
   * (NEX-1084), deshalb braucht der Draft diesen Stand neben sich.
   */
  private readonly savedCondition = signal<StepCondition | null>(null);

  /**
   * Gate fuer den Inline-Override-Editor. Ohne Kontext-Step gibt es keinen Step,
   * in den {@link propagateOverride} schreiben koennte — der Editor nahm Eingaben
   * an, zeigte „Eingeschraenkt" und verwarf sie still (NEX-1052-Review).
   */
  readonly tdbEditingEnabled = computed(() => !!this.currentContextStep());

  /** Stand des Entwurfs, der zuletzt an den Schritt ging (beim Öffnen: der geseedete). */
  private readonly appliedTdb = signal<TdbDraftState>(EMPTY_TDB);

  readonly tdbDirty = computed(
    () => this.tdbEditingEnabled() && tdbSignature(this.currentTdb()) !== tdbSignature(this.appliedTdb()),
  );

  readonly tdbDraftIncomplete = computed(() => missingConditionFields(this.overrideDraft()).length > 0);

  readonly showTdbApplyBar = computed(() => !this.readOnly() && this.tdbDirty());

  readonly topBlockRows = computed<TestdatenBlockRow[]>(() => {
    const activeId = this.activeSheetId();
    const draft = this.overrideDraft();
    return this.drawerLinkedSheets().map((sheet) => ({
      tdb: toTestdatenblattSummary(sheet),
      override: sheet.id === activeId ? draft : {},
    }));
  });

  readonly topBlockTitleKey = computed(() => (this.canGoBack() ? 'tdb.title.module' : 'tdb.title.testcase'));

  /**
   * Follow-up: Modul↔TDB-Verknuepfung direkt aus dem Drawer heraus. Die Liste
   * wird pro Drawer-Session geladen; Verbinden/Trennen persistiert sofort via
   * `TestdatenblattService.linkModule` / `unlinkModule`, nur fuer dieses eine Modul.
   */
  readonly drawerAllSheets = signal<Testdatenblatt[]>([]);
  private readonly sheetLinkWrites = new Subject<SheetLinkChange>();
  /** Lokale Verknuepfungs-Aenderungen, die eine laufende list()-Antwort noch nicht kennen kann. */
  private sheetLinkChanges: SheetLinkChange[] = [];
  private sheetLinkChangeSeq = 0;
  private sheetsLoadCount = 0;
  readonly tdbLinkSearch = signal('');
  readonly tdbLinkOpen = signal(false);

  /**
   * NEX-1052-Review (sgraetz): kein Fallback mehr auf {@link availableSheets}.
   * Vorher: bei leerem {@code drawerAllSheets} (Ladefehler oder Request in flight)
   * zog die Fallback-Kette die Sheets aus dem Parent-Snapshot; ein Klick auf
   * „Trennen" bei einer so gerenderten Zeile emittierte {@code [...[], updated]}
   * = **ein** Element, der Parent ersetzte damit seine gesamte Liste. Der Seed
   * in {@link loadDrawerSheets} garantiert, dass die interne Liste nie leer ist.
   */
  private sheetsForActiveModule(): Testdatenblatt[] {
    const modId = this.activeModuleId();
    if (!modId) return [];
    return this.drawerAllSheets().filter((s) => s.testModuleIds?.includes(modId));
  }

  readonly drawerLinkedSheets = computed<Testdatenblatt[]>(() => this.sheetsForActiveModule());

  /** Singular/Plural fuer die Live-Region am Zaehler (Repo-Konvention `…One` / `…Other`). */
  readonly linkedCountKey = computed(() =>
    this.drawerLinkedSheets().length === 1
      ? 'modules.drawer.tdbLinking.countAnnouncementOne'
      : 'modules.drawer.tdbLinking.countAnnouncementOther',
  );

  /**
   * Optionen der Verbinden-Combobox: ALLE Blaetter des Projekts, nur nach dem
   * Suchbegriff gefiltert. Die bereits verknuepften bleiben drin und werden als
   * {@code aria-selected} + {@code disabled} gezeigt — eine Listbox, aus der der
   * gewaehlte Eintrag verschwindet, kann ihren Zustand nicht ausdruecken, und
   * {@code aria-selected} waere strukturell immer {@code false}
   * (NEX-1052-Review Runde 3).
   */
  readonly drawerLinkOptions = computed<Testdatenblatt[]>(() => {
    const modId = this.activeModuleId();
    if (!modId) return [];
    const term = this.tdbLinkSearch().trim().toLowerCase();
    return this.drawerAllSheets().filter(
      (s) => !term || s.key.toLowerCase().includes(term) || s.name.toLowerCase().includes(term),
    );
  });

  /** True, wenn das Blatt bereits am aktiven Modul haengt — traegt {@code aria-selected}. */
  isSheetLinkedToActiveModule(sheet: Testdatenblatt): boolean {
    const modId = this.activeModuleId();
    return !!modId && !!this.liveSheet(sheet).testModuleIds?.includes(modId);
  }

  private readonly titleInput = viewChild<ElementRef<HTMLInputElement>>('titleInput');
  private readonly saveButton = viewChild<ElementRef<HTMLButtonElement>>('saveButton');
  private readonly cancelButton = viewChild<ElementRef<HTMLButtonElement>>('cancelButton');

  constructor() {
    this.sheetLinkWrites
      .pipe(
        groupBy((write) => write.sheetId),
        mergeMap((writesForSheet) => writesForSheet.pipe(concatMap((write) => this.writeSheetLink(write)))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
    this.accountService
      .getProfile()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (profile) => this.self.set({ tenantUserId: profile.id, displayName: profile.displayName }),
        error: () => this.self.set(null),
      });
    effect(() => {
      const id = this.moduleId();
      if (id) {
        this.triggerElement = (document.activeElement as HTMLElement) ?? null;
        this.stack.set([]);
        // Kontext-Step, Draft und Sheet-Seed nur beim Oeffnen aus den Inputs lesen.
        // `untracked` verhindert, dass spaetere Aenderungen an ihnen (etwa nach
        // eigenem Emit → Parent-Update → Input-Refresh) diesen Effect erneut feuern
        // und den laufenden Editier-Draft ueberschreiben.
        //
        // Konsequenz: `moduleId` ist der einzige Oeffnungs-Trigger. Ein Direktwechsel
        // von Zeile zu Zeile auf dasselbe Modul muss `moduleId` zwischendurch auf
        // null setzen, sonst bleibt der alte Kontext-Step stehen.
        const initialStep = untracked(() => this.contextStep());
        this.currentContextStep.set(initialStep);
        this.seedOverrideDraftFrom(
          initialStep,
          untracked(() => this.contextOverride()),
        );
        this.loadModule(id);
        this.loadDrawerSheets(untracked(() => this.availableSheets()));
      }
    });
  }

  private seedOverrideDraftFrom(step: TestStep | null, rowDraft: TdbOverride | null = null): void {
    const seeded: TdbDraftState = step
      ? {
          draft: rowDraft ?? {
            ...fromStepCondition(step.stepCondition ?? null),
            filter: formatFilterExpression(step.dataSheetFilters),
          },
          sheetId: step.dataSheetId ?? null,
          condition: step.stepCondition ?? null,
        }
      : EMPTY_TDB;
    this.restoreTdb(seeded);
    this.appliedTdb.set(seeded);
  }

  private currentTdb(): TdbDraftState {
    return { draft: this.overrideDraft(), sheetId: this.activeSheetId(), condition: this.savedCondition() };
  }

  private restoreTdb(state: TdbDraftState): void {
    this.overrideDraft.set(state.draft);
    this.activeSheetId.set(state.sheetId);
    this.savedCondition.set(state.condition);
  }

  // ── Testdaten-Block des aufrufenden Schritts ──────────────────────────────

  /** True fuer die genau eine TDB, deren Override gerade den Step besetzt. */
  isSheetActiveOverride(sheet: Testdatenblatt): boolean {
    return this.activeSheetId() === sheet.id;
  }

  onTopBlockChanged(event: { tdbId: string; patch: Partial<TdbOverride> }): void {
    const sheet = this.sheetByKey(event.tdbId);
    if (sheet) this.applyDraftFor(sheet, event.patch);
  }

  onTopBlockReset(tdbId: string): void {
    const sheet = this.sheetByKey(tdbId);
    if (sheet) this.resetSheetOverride(sheet);
  }

  onTopBlockUnlink(tdbId: string): void {
    const sheet = this.sheetByKey(tdbId);
    if (sheet) this.unlinkSheet(sheet);
  }

  /** Der Block kennt nur den Key; der Key ist im Projekt eindeutig. */
  private sheetByKey(key: string): Testdatenblatt | undefined {
    return this.drawerAllSheets().find((s) => s.key === key);
  }

  private resetSheetOverride(sheet: Testdatenblatt): void {
    if (!this.isSheetActiveOverride(sheet) || this.readOnly()) return;
    this.restoreTdb(EMPTY_TDB);
  }

  /** Wechsel auf die eben angefasste TDB (Single-Slot am BE); der Schritt erfaehrt es erst beim Uebernehmen. */
  private applyDraftFor(sheet: Testdatenblatt, patch: Partial<TdbOverride>): void {
    if (this.readOnly()) return;
    // Die Bedingung haengt am Step, nicht am Blatt: Beim Wechsel des Blatts geht nur die Zeilen-Auswahl.
    const previousDraft = this.isSheetActiveOverride(sheet)
      ? this.overrideDraft()
      : withoutFilter(this.overrideDraft());
    const nextDraft: TdbOverride = { ...previousDraft, ...patch };
    this.restoreTdb({
      draft: nextDraft,
      sheetId: sheet.id,
      condition: nextStepCondition(this.savedCondition(), patch, nextDraft),
    });
  }

  applyTdbDraft(): void {
    if (this.readOnly() || !this.tdbDirty()) return;
    this.commitTdbDraft();
    this.focusSaveButton();
  }

  cancelTdbDraft(): void {
    this.restoreTdb(this.appliedTdb());
    this.focusSaveButton();
  }

  /** Speichern nimmt einen offenen Entwurf mit, auch einen halben. */
  private flushTdbDraft(): void {
    if (!this.readOnly() && this.tdbDirty()) this.commitTdbDraft();
  }

  private commitTdbDraft(): void {
    const state = this.currentTdb();
    const step = this.currentContextStep();
    const saved = step?.dataSheetId === state.sheetId ? step?.dataSheetFilters : null;
    const delta =
      state.sheetId === null
        ? { dataSheetId: null, dataSheetFilters: null, stepCondition: state.condition }
        : {
            dataSheetId: state.sheetId,
            dataSheetFilters: filterEntriesFor(state.draft.filter, saved),
            stepCondition: state.condition,
          };
    this.propagateOverride(delta, missingConditionFields(state.draft).length > 0 ? state.draft : undefined);
    this.appliedTdb.set(state);
  }

  /** Die Uebernehmen-Leiste verschwindet mit dem Klick; ohne das ginge der Fokus verloren. */
  private focusSaveButton(): void {
    const save = this.saveButton()?.nativeElement;
    (save && !save.disabled ? save : this.cancelButton()?.nativeElement)?.focus();
  }

  /** Gibt den Entwurf an die Zeile des Modul-Editors; `currentContextStep` bleibt, sonst feuerte der Seed-Effect. */
  private propagateOverride(delta: Omit<TdbOverrideDelta, 'stepId' | 'override'>, incompleteDraft?: TdbOverride): void {
    const step = this.currentContextStep();
    if (!step) return;
    this.tdbOverrideApplied.emit(
      incompleteDraft ? { stepId: step.id, ...delta, override: incompleteDraft } : { stepId: step.id, ...delta },
    );
  }

  onTdbLinkSearch(event: Event): void {
    this.tdbLinkSearch.set((event.target as HTMLInputElement).value);
    this.tdbLinkOpen.set(true);
  }

  /**
   * NEX-1052-Review (sgraetz): {@code focusout} am Container statt {@code blur} am
   * Suchfeld. Der frueher noetige {@code setTimeout} schloss die Liste auch dann,
   * wenn der Fokus per Tab auf eine Option wanderte — die Optionen waren damit per
   * Tastatur unerreichbar. Zielt der Fokus in den Container, bleibt sie offen.
   */
  onTdbLinkFocusOut(event: FocusEvent): void {
    if (this.staysInsideCombobox(event)) return;
    this.tdbLinkOpen.set(false);
  }

  private staysInsideCombobox(event: FocusEvent): boolean {
    const container = event.currentTarget as HTMLElement | null;
    const next = event.relatedTarget as Node | null;
    return !!container && !!next && container.contains(next);
  }

  linkSheet(sheet: Testdatenblatt): void {
    const modId = this.activeModuleId();
    if (!modId) return;
    this.linkTo(modId, sheet);
    this.tdbLinkSearch.set('');
    this.tdbLinkOpen.set(false);
  }

  unlinkSheet(sheet: Testdatenblatt): void {
    const modId = this.activeModuleId();
    if (!modId) return;
    const step = this.currentContextStep();
    this.changeSheetLink(this.liveSheet(sheet), modId, false, () => {
      if (step && this.currentContextStep() === step) this.detachSheetFromCall(sheet.id);
      this.detachSheetFromCalls(modId, sheet.id);
    });
  }

  /** Alle Ebenen des Drawers und der Aufrufer verlieren das Blatt an Aufrufen dieses Moduls (NEX-1796). */
  private detachSheetFromCalls(moduleId: string, sheetId: string): void {
    const detach = (steps: TestStep[]): TestStep[] =>
      steps.map((s) =>
        s.kind === 'MODULE_REF' && s.calledModuleId === moduleId && s.dataSheetId === sheetId
          ? { ...s, dataSheetId: null, dataSheetFilters: null }
          : s,
      );
    this.steps.update(detach);
    this.stack.update((stack) => stack.map((entry) => ({ ...entry, steps: detach(entry.steps) })));
    this.sheetUnlinked.emit({ moduleId, sheetId });
  }

  /** Die Zeile des Modul-Editors verliert das Blatt; die Seite gilt danach als geaendert. */
  private detachSheetFromCall(sheetId: string): void {
    if (this.activeSheetId() === sheetId) this.restoreTdb(withoutSheet(this.currentTdb()));
    const applied = this.appliedTdb();
    if (applied.sheetId !== sheetId) return;
    const detached = withoutSheet(applied);
    this.propagateOverride(
      { dataSheetId: null, dataSheetFilters: null, stepCondition: detached.condition },
      missingConditionFields(detached.draft).length > 0 ? detached.draft : undefined,
    );
    this.appliedTdb.set(detached);
  }

  /**
   * Verknuepft gegen den *aktuellen* Stand aus {@link drawerAllSheets}. Die Option
   * aus der Combobox ist ein Snapshot; ein zweiter Trigger auf dasselbe Blatt lief
   * sonst am Duplikat-Guard vorbei und schickte ein zweites PUT.
   */
  private linkTo(moduleId: string, sheet: Testdatenblatt): void {
    const current = this.liveSheet(sheet);
    if ((current.testModuleIds ?? []).includes(moduleId)) return;
    this.changeSheetLink(current, moduleId, true);
  }

  private liveSheet(sheet: Testdatenblatt): Testdatenblatt {
    return this.drawerAllSheets().find((s) => s.id === sheet.id) ?? sheet;
  }

  /**
   * Zeigt die Aenderung sofort an und reiht den Schreibvorgang in die Warteschlange
   * des Blatts ein. Pro Blatt laeuft nur ein Request: Verbinden und sofortiges
   * Trennen kaemen sonst in beliebiger Reihenfolge am Server an.
   */
  private changeSheetLink(sheet: Testdatenblatt, moduleId: string, link: boolean, onSuccess?: () => void): void {
    const wasKnown = this.drawerAllSheets().some((s) => s.id === sheet.id);
    if (!wasKnown) this.drawerAllSheets.update((list) => [...list, sheet]);
    const change = this.recordSheetLinkChange(sheet.id, moduleId, link, wasKnown, false);
    change.onSuccess = onSuccess;
    // Parent-`availableSheets` speist die Test-Step-Table-Chips.
    this.sheetsChanged.emit(this.drawerAllSheets());
    this.sheetLinkWrites.next(change);
  }

  private recordSheetLinkChange(
    sheetId: string,
    moduleId: string,
    link: boolean,
    wasKnown: boolean,
    settled: boolean,
  ): SheetLinkChange {
    const change: SheetLinkChange = { seq: ++this.sheetLinkChangeSeq, sheetId, moduleId, link, wasKnown, settled };
    this.sheetLinkChanges.push(change);
    this.drawerAllSheets.update((list) =>
      list.map((s) =>
        s.id === sheetId ? { ...s, testModuleIds: withModuleLink(s.testModuleIds, moduleId, link) } : s,
      ),
    );
    return change;
  }

  private writeSheetLink(change: SheetLinkChange): Observable<unknown> {
    const request$ = change.link
      ? this.testdatenblattService.linkModule(this.projectId(), change.sheetId, change.moduleId)
      : this.testdatenblattService.unlinkModule(this.projectId(), change.sheetId, change.moduleId);
    return request$.pipe(
      tap(() => change.onSuccess?.()),
      catchError(() => {
        this.rollBackSheetLink(change);
        return EMPTY;
      }),
      finalize(() => (change.settled = true)),
    );
  }

  /** Nimmt nur diesen einen Klick zurueck, auf dem aktuellen Stand des Blatts. */
  private rollBackSheetLink(change: SheetLinkChange): void {
    if (change.wasKnown) {
      this.recordSheetLinkChange(change.sheetId, change.moduleId, !change.link, true, true);
    } else {
      this.drawerAllSheets.update((list) => list.filter((s) => s.id !== change.sheetId));
    }
    this.sheetsChanged.emit(this.drawerAllSheets());
    this.snackBar.open(this.translateService.instant('modules.drawer.tdb.linkError'), undefined, {
      duration: 4000,
    });
  }

  /**
   * NEX-1052-Review (sgraetz): Zweistufig:
   * 1. Sofort aus dem Parent-Snapshot seeden — so hat {@link sheetsForActiveModule}
   *    immer eine valide Liste und ein Fehlklick auf „Trennen" kann den Parent-
   *    Snapshot nicht durch ein Ein-Element-Array ersetzen.
   * 2. Danach live nachladen; im Fehlerfall meldet der Snackbar den Ausfall und
   *    der Seed bleibt bestehen, statt still zu einer leeren Liste zu fallen.
   *
   * <p>Der Seed kommt als Parameter herein, damit der Aufrufer im Effect ihn
   * {@code untracked} liest: gelesen wuerde {@link availableSheets} sonst zur
   * Effect-Abhaengigkeit, und jedes eigene {@link sheetsChanged} liefe ueber den
   * Parent zurueck in einen kompletten Drawer-Reload (verwarf getippte Eingaben).
   */
  private loadDrawerSheets(seed: readonly Testdatenblatt[]): void {
    this.drawerAllSheets.set([...seed]);
    const requestedAt = this.sheetLinkChangeSeq;
    const load = ++this.sheetsLoadCount;
    this.testdatenblattService
      .list(this.projectId())
      .pipe(
        catchError(() => {
          this.snackBar.open(this.translateService.instant('modules.drawer.tdb.sheetsLoadError'), undefined, {
            duration: 4000,
          });
          return of<Testdatenblatt[] | null>(null);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((sheets) => {
        if (sheets !== null && load === this.sheetsLoadCount) this.mergeLoadedSheets(sheets, requestedAt);
      });
  }

  /**
   * Die Antwort kann aelter sein als Klicks, die waehrend des Ladens kamen oder
   * noch nicht bestaetigt sind. Diese Aenderungen werden auf den Server-Stand
   * nachgespielt, alles andere kommt vom Server.
   */
  private mergeLoadedSheets(sheets: Testdatenblatt[], requestedAt: number): void {
    this.sheetLinkChanges = this.sheetLinkChanges.filter((c) => !c.settled || c.seq > requestedAt);
    const replay = this.sheetLinkChanges;
    this.drawerAllSheets.set(
      sheets.map((sheet) =>
        replay
          .filter((c) => c.sheetId === sheet.id)
          .reduce((s, c) => ({ ...s, testModuleIds: withModuleLink(s.testModuleIds, c.moduleId, c.link) }), sheet),
      ),
    );
    if (replay.length > 0) this.sheetsChanged.emit(this.drawerAllSheets());
  }

  ngOnInit(): void {
    this.projectMemberService
      .getMembers(this.projectId())
      .pipe(
        catchError(() => {
          this.snackBar.open(this.translateService.instant('modules.drawer.usersLoadError'), undefined, {
            duration: 4000,
          });
          return of([] as ProjectMemberDTO[]);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((users) => this.users.set(users));
  }

  /** NEX-692: tritt der Präsenz des aktiven Moduls bei und verlässt ggf. das vorherige. */
  private enterPresence(moduleId: string): void {
    const me = this.self();
    if (!me || this.joinedModuleId === moduleId) {
      return;
    }
    this.exitPresence();
    this.presenceService.join(moduleId, me);
    this.joinedModuleId = moduleId;
  }

  private exitPresence(): void {
    const me = this.self();
    if (me && this.joinedModuleId) {
      this.presenceService.leave(this.joinedModuleId, me);
    }
    this.joinedModuleId = null;
  }

  isModuleItem(step: TestStep): boolean {
    return step.kind === 'MODULE_REF';
  }

  // ── Bedingungen am verschachtelten MODULE_REF-Step ────────────────────────

  /**
   * Zeilen der Bedingungsliste je Modulaufruf, solange der Nutzer sie bearbeitet. Halbe
   * Zeilen ueberdauern das Hinein- und Hinausgehen, auch ein Verwerfen der Ebene, und
   * gelten fuer jede Ebene (NEX-1682); geleert wird erst beim Schliessen.
   */
  private readonly moduleStepDrafts = signal<Record<string, TdbOverride[]>>({});

  /** Positionen der Modulaufrufe dieser Ebene mit halber Bedingung; Speichern sendet dann nichts. */
  readonly incompleteConditionSteps = computed<number[]>(() => {
    if (this.readOnly()) return [];
    const drafts = this.moduleStepDrafts();
    return this.steps().flatMap((s, i) => (s.kind === 'MODULE_REF' && hasHalfRow(drafts[s.id]) ? [i + 1] : []));
  });

  private readonly conditionLists = viewChildren(StepConditionListComponent);

  /** Objekt- und Fensterauswahl, geladen erst, wenn eine Zeile sie braucht. */
  readonly conditionLibrary = signal<readonly ConditionLibrary[] | null>(null);
  private conditionLibraryRequested = false;
  private readonly loadConditionLibrary = effect(() => {
    if (this.conditionLibraryRequested) return;
    const needed = this.steps().some(
      (s) => s.kind === 'MODULE_REF' && this.conditionRows(s).some((row) => !!row.subj && row.subj !== 'WERT'),
    );
    if (!needed) return;
    this.conditionLibraryRequested = true;
    // Erst hier geholt: Der Dienst haengt am Router und wird nur gebraucht, wenn eine Zeile ein Objekt prueft.
    this.injector
      .get(ObjectMappingService)
      .conditionLibrary(this.projectId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (libs) => this.conditionLibrary.set(libs),
        error: () => (this.conditionLibraryRequested = false),
      });
  });

  /** Sub-Modul-Chip: pro Step-ID der Search-Text + Open-Zustand der Verbinden-Combobox. */
  readonly moduleStepLinkSearch = signal<Record<string, string>>({});
  readonly moduleStepLinkOpen = signal<Record<string, boolean>>({});

  moduleStepLinkedSheets(step: TestStep): Testdatenblatt[] {
    const modId = step.calledModuleId;
    if (!modId) return [];
    return this.drawerAllSheets().filter((s) => s.testModuleIds?.includes(modId));
  }

  /** Ausgeschaltete Datenzeilen laufen nicht und zaehlen deshalb nicht mit (NEX-2262). */
  activeRows(sheet: Testdatenblatt): number {
    return activeRowCount(sheet.zeilen.length, sheet.zeilenAktiv);
  }

  conditionRows(step: TestStep): TdbOverride[] {
    return this.moduleStepDrafts()[step.id] ?? fromStepConditions(stepConditionsOf(step)).rows;
  }

  moduleStepConditionPreview(step: TestStep): string {
    const fragments = this.conditionRows(step).flatMap((row) => bedingungFragment(row) ?? []);
    return formatTcdlPreview(this.moduleItemName(step), '', fragments);
  }

  /** Immer die ganze Liste; halbe Zeilen fehlen darin, bis sie vollstaendig sind. */
  onModuleStepConditionsChange(step: TestStep, rows: TdbOverride[]): void {
    if (this.readOnly()) return;
    this.moduleStepDrafts.update((drafts) => ({ ...drafts, [step.id]: rows }));
    this.steps.update((list) =>
      list.map((s) => {
        if (s.id !== step.id) return s;
        const stepConditions = toStepConditions(rows, stepConditionsOf(s));
        return { ...s, stepConditions, stepCondition: stepConditions[0] ?? null };
      }),
    );
  }

  private focusFirstMissingCondition(): void {
    this.conditionLists().some((list) => list.focusFirstMissing());
  }

  // ── Verbinden/Trennen von TDBs am verschachtelten MODULE_REF-Step ─────────

  /** Wie {@link drawerLinkOptions}, aber fuer die Combobox am Sub-Modul-Chip. */
  moduleStepLinkOptions(step: TestStep): Testdatenblatt[] {
    if (!step.calledModuleId) return [];
    const term = (this.moduleStepLinkSearch()[step.id] ?? '').trim().toLowerCase();
    return this.drawerAllSheets().filter(
      (s) => !term || s.key.toLowerCase().includes(term) || s.name.toLowerCase().includes(term),
    );
  }

  isSheetLinkedToModuleStep(step: TestStep, sheet: Testdatenblatt): boolean {
    const modId = step.calledModuleId;
    return !!modId && !!this.liveSheet(sheet).testModuleIds?.includes(modId);
  }

  moduleStepLinkSearchFor(step: TestStep): string {
    return this.moduleStepLinkSearch()[step.id] ?? '';
  }

  moduleStepLinkOpenFor(step: TestStep): boolean {
    return !!this.moduleStepLinkOpen()[step.id];
  }

  onModuleStepLinkSearch(step: TestStep, event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.moduleStepLinkSearch.update((m) => ({ ...m, [step.id]: value }));
    this.moduleStepLinkOpen.update((m) => ({ ...m, [step.id]: true }));
  }

  onModuleStepLinkFocus(step: TestStep): void {
    this.moduleStepLinkOpen.update((m) => ({ ...m, [step.id]: true }));
  }

  onModuleStepLinkFocusOut(step: TestStep, event: FocusEvent): void {
    if (this.staysInsideCombobox(event)) return;
    this.moduleStepLinkOpen.update((m) => ({ ...m, [step.id]: false }));
  }

  moduleStepLinkSheet(step: TestStep, sheet: Testdatenblatt): void {
    const modId = step.calledModuleId;
    if (!modId) return;
    this.linkTo(modId, sheet);
    this.moduleStepLinkSearch.update((m) => ({ ...m, [step.id]: '' }));
    this.moduleStepLinkOpen.update((m) => ({ ...m, [step.id]: false }));
  }

  moduleStepUnlinkSheet(step: TestStep, sheet: Testdatenblatt): void {
    const modId = step.calledModuleId;
    if (!modId) return;
    this.changeSheetLink(this.liveSheet(sheet), modId, false, () => this.detachSheetFromCalls(modId, sheet.id));
  }

  statusFor(step: TestStep): TestStepStatus {
    return fromRunStatus(step.status);
  }

  moduleItemName(step: TestStep): string {
    return step.title?.trim() || step.calledModuleId || '';
  }

  updateTitle(value: string): void {
    this.title.set(value);
  }

  updateDescription(value: string): void {
    this.description.set(value);
  }

  onTitleInput(event: Event): void {
    this.updateTitle((event.target as HTMLInputElement).value);
  }

  onDescriptionInput(event: Event): void {
    this.updateDescription((event.target as HTMLTextAreaElement).value);
  }

  onStepFieldInput(index: number, field: 'title' | 'action' | 'expectedResult' | 'notes', event: Event): void {
    const value = (event.target as HTMLInputElement | HTMLTextAreaElement).value;
    // Titel bleibt als String erhalten (auch leer); die übrigen Freitextfelder werden zu null.
    const normalized = field === 'title' ? value : value.length ? value : null;
    this.patchStep(index, { [field]: normalized });
  }

  onStepStatusChange(index: number, event: Event): void {
    const status = (event.target as HTMLSelectElement).value as TestStepStatus;
    this.patchStep(index, { status: toRunStatus(status) });
  }

  addStep(): void {
    const id = nextLocalId(++this.localIdCounter);
    const newStep = createLocalStep(id, this.steps().length, '', null, null, { title: '' });
    this.steps.update((list) => [...list, newStep]);
  }

  toggleEmbedPanel(): void {
    this.embedPanelOpen.update((open) => !open);
  }

  closeEmbedPanel(): void {
    this.embedPanelOpen.set(false);
  }

  /** NEX-690: hängt das gewählte Modul als MODULE_REF-Zeile ans Ende der Ablaufliste an. */
  onEmbedApplied(module: TestModule): void {
    const id = nextLocalId(++this.localIdCounter);
    const row = createLocalModuleRefStep(id, this.steps().length, module.id, module.name);
    this.steps.update((list) => [...list, row]);
    this.closeEmbedPanel();
  }

  moveItem(index: number, delta: number): void {
    const target = index + delta;
    const list = this.steps();
    if (target < 0 || target >= list.length) {
      return;
    }
    const next = [...list];
    [next[index], next[target]] = [next[target], next[index]];
    this.steps.set(next);
  }

  removeItem(index: number): void {
    this.steps.update((list) => list.filter((_, i) => i !== index));
  }

  /** Öffnet ein eingebundenes Sub-Modul im selben Drawer und legt den aktuellen Kontext auf den Stack. */
  openSubModule(step: TestStep): void {
    if (!step.calledModuleId || this.busy()) {
      return;
    }
    if (this.isAlreadyOpen(step.calledModuleId)) {
      this.snackBar.open(this.translateService.instant('modules.drawer.alreadyOpen'), undefined, { duration: 4000 });
      return;
    }
    this.stack.update((stack) => [
      ...stack,
      {
        moduleId: this.activeModuleId() ?? '',
        title: this.title(),
        description: this.description(),
        authorUserId: this.authorControl.value,
        steps: this.steps(),
        meta: this.meta(),
        signature: this.originalSignature(),
        contextStep: this.currentContextStep(),
        tdb: { current: this.currentTdb(), applied: this.appliedTdb() },
      },
    ]);
    // Testdatenblaetter eines Sub-Moduls sind nur Auswahl; Bedingungen traegt die Ebene darunter (NEX-2418).
    this.currentContextStep.set(null);
    this.seedOverrideDraftFrom(null);
    this.loadModule(step.calledModuleId);
  }

  /**
   * Ein zweiter Entwurf desselben Moduls im Stapel würde beim Zurückgehen die gespeicherte
   * Fassung überschreiben — deshalb öffnet ein Modul nur einmal.
   */
  private isAlreadyOpen(moduleId: string): boolean {
    return (
      moduleId === this.activeModuleId() ||
      moduleId === this.hostModuleId() ||
      this.stack().some((entry) => entry.moduleId === moduleId)
    );
  }

  /** Kehrt in den Aufrufer-Kontext zurück; bei ungespeicherten Änderungen mit Rückfrage. */
  back(): void {
    if (!this.canGoBack()) {
      return;
    }
    if (this.dirty()) {
      this.discardTarget.set('back');
      return;
    }
    this.doBack();
  }

  requestClose(): void {
    if (this.dirty() || this.outerDirty()) {
      this.discardTarget.set('close');
      return;
    }
    this.doClose();
  }

  onOverlayClick(): void {
    this.requestClose();
  }

  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.stopPropagation();
      if (this.discardTarget()) {
        this.cancelDiscard();
      } else {
        this.requestClose();
      }
    }
  }

  /** Die Rueckfrage liegt ausserhalb des Panels; Esc schliesst dort nur sie. */
  onConfirmKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape') return;
    event.stopPropagation();
    this.cancelDiscard();
  }

  cancelDiscard(): void {
    this.discardTarget.set(null);
  }

  confirmDiscard(): void {
    const target = this.discardTarget();
    this.discardTarget.set(null);
    if (target === 'back') {
      this.doBack();
    } else {
      this.doClose();
    }
  }

  save(): void {
    this.requestSave(null);
  }

  /** Speichern aus der Verwerfen-Rückfrage heraus: persistiert und führt danach das Ziel aus. */
  saveFromConfirm(): void {
    const target = this.discardTarget();
    this.discardTarget.set(null);
    if (target && !this.moduleDirty()) {
      this.flushTdbDraft();
      this.leave(target);
      return;
    }
    this.requestSave(target);
  }

  /**
   * NEX-688: Wird das Modul mehrfach verwendet, muss der Save explizit bestätigt werden,
   * da er alle Verwender betrifft. Sonst direkt persistieren.
   */
  private requestSave(postAction: DiscardTarget | null): void {
    if (this.busy()) {
      return;
    }
    // NEX-824: guards the method itself (not just the disabled save button),
    // in case save()/saveFromConfirm() is ever invoked programmatically.
    if (this.authorControl.invalid) {
      this.authorControl.markAsTouched();
      return;
    }
    if (this.incompleteConditionSteps().length > 0) {
      this.focusFirstMissingCondition();
      return;
    }
    if ((this.meta()?.usageCount ?? 0) > 1) {
      this.pendingSaveAction = postAction;
      this.multiUseOpen.set(true);
      return;
    }
    this.persist(postAction);
  }

  onMultiUseConfirmed(): void {
    this.multiUseOpen.set(false);
    this.persist(this.pendingSaveAction);
    this.pendingSaveAction = null;
  }

  private persist(postAction: DiscardTarget | null): void {
    const moduleId = this.activeModuleId();
    if (!moduleId) {
      return;
    }
    this.flushTdbDraft();
    this.busy.set(true);
    this.resetSaveErrors();
    const request = toSaveFullModuleRequest(
      {
        title: this.title().trim(),
        description: this.description().trim(),
        status: this.meta()?.status ?? 'DRAFT',
        authorUserId: this.authorControl.value,
        tags: this.meta()?.tags ?? [],
        automationStatus: this.meta()?.automationStatus ?? 'MANUAL',
        // Der Drawer hat keine eigene Automation-UI — die vom Modul geladenen
        // Werte werden unverändert mitgeschickt, damit ein Save hier sie nicht
        // auf null zurücksetzt.
        automationFramework: this.meta()?.automationFramework ?? null,
      },
      this.steps(),
    );
    // Immer die ganze Liste: Mit nur `stepCondition` ersetzte der Server nur die erste Bedingung.
    request.steps = request.steps.map((draft, i) =>
      draft.kind === 'MODULE_REF' ? { ...draft, stepConditions: stepConditionsOf(this.steps()[i]) } : draft,
    );
    this.moduleService
      .saveFull(this.projectId(), moduleId, request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.busy.set(false);
          // response.module ist die Modul-Zusammenfassung OHNE Ablauf; die frisch
          // persistierten Schritte (inkl. neu angelegter, mit finalen UUIDs) und Embeds
          // stehen in response.steps/response.embeds. Für den Live-Store zusammenführen.
          const saved: TestModuleDetail = {
            ...response.module,
            steps: response.steps,
            embeddedModules: response.embeds,
          };
          this.afterSave(saved, postAction);
        },
        error: (error: unknown) => {
          this.busy.set(false);
          this.saveErrorKey.set(saveErrorKeyFor(error));
          this.saveError.set(true);
        },
      });
  }

  private resetSaveErrors(): void {
    this.saveError.set(false);
    this.saveErrorKey.set(SAVE_ERROR_GENERIC);
  }

  private afterSave(module: TestModuleDetail, postAction: DiscardTarget | null): void {
    // NEX-691: Live-Store aktualisieren, damit offene Ansichten (Testfall-Tabelle,
    // Eltern-Drawer-Ebene) die Änderung ohne Reload spiegeln.
    this.moduleStore.upsert(module);
    this.saved.emit(module);
    this.leave(postAction ?? 'close');
  }

  /** Schließen endet auf der Ebene darunter, solange dort etwas ungespeichert ist – es ginge sonst still verloren. */
  private leave(target: DiscardTarget): void {
    if (target === 'back' || this.outerDirty()) {
      this.doBack();
      return;
    }
    this.doClose();
  }

  private doBack(): void {
    const stack = this.stack();
    const entry = stack[stack.length - 1];
    if (!entry) {
      return;
    }
    this.stack.set(stack.slice(0, -1));
    this.activeModuleId.set(entry.moduleId);
    this.title.set(entry.title);
    this.description.set(entry.description);
    this.authorControl.setValue(entry.authorUserId);
    this.steps.set(entry.steps);
    this.meta.set(entry.meta);
    this.originalSignature.set(entry.signature);
    this.currentContextStep.set(entry.contextStep);
    this.restoreTdb(entry.tdb.current);
    this.appliedTdb.set(entry.tdb.applied);
    this.resetSaveErrors();
    this.enterPresence(entry.moduleId);
  }

  private doClose(): void {
    this.exitPresence();
    this.stack.set([]);
    this.discardTarget.set(null);
    this.resetSaveErrors();
    this.resetComboboxes();
    this.moduleStepDrafts.set({});
    this.activeModuleId.set(null);
    const trigger = this.triggerElement;
    this.triggerElement = null;
    trigger?.focus?.();
    this.closed.emit();
  }

  /**
   * Such- und Auf-/Zu-Zustand beider Comboboxen. Seit die Liste nur noch ueber
   * {@code focusout} schliesst, ueberdauert ein offener Zustand sonst das Schliessen
   * des Drawers und die naechste Session startet mit aufgeklappter Liste.
   */
  private resetComboboxes(): void {
    this.tdbLinkOpen.set(false);
    this.tdbLinkSearch.set('');
    this.moduleStepLinkOpen.set({});
    this.moduleStepLinkSearch.set({});
  }

  private loadModule(id: string): void {
    this.activeModuleId.set(id);
    this.moduleStepDrafts.update(incompleteOnly);
    this.loading.set(true);
    this.resetSaveErrors();
    this.moduleService
      .getById(this.projectId(), id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (detail) => {
          this.applyDetail(detail);
          this.loading.set(false);
          this.enterPresence(id);
          queueMicrotask(() => this.titleInput()?.nativeElement.focus());
        },
        error: () => {
          this.loading.set(false);
          this.saveError.set(true);
        },
      });
  }

  private applyDetail(detail: TestModuleDetail): void {
    const steps = [...detail.steps].sort((a, b) => a.orderIndex - b.orderIndex);
    const authorUserId = detail.authorUserId ?? '';
    this.title.set(detail.name);
    this.description.set(detail.description ?? '');
    this.authorControl.setValue(authorUserId);
    this.steps.set(steps);
    this.meta.set(detail);
    this.originalSignature.set(this.signatureOf(detail.name, detail.description ?? '', authorUserId, steps));
  }

  private patchStep(index: number, patch: Partial<TestStep>): void {
    this.steps.update((list) => list.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  }

  private signatureOf(title: string, description: string, authorUserId: string, steps: TestStep[]): string {
    return JSON.stringify({
      title,
      description,
      authorUserId,
      steps: steps.map((s) => ({
        id: isLocalId(s.id) ? null : s.id,
        kind: s.kind,
        title: s.title ?? null,
        action: s.action ?? null,
        expectedResult: s.expectedResult ?? null,
        notes: s.notes ?? null,
        status: s.status,
        calledModuleId: s.calledModuleId ?? null,
        // NEX-1052 Follow-up: TDB-Override an MODULE_REF-Steps ist Teil der
        // Dirty-Rechnung. Ohne diese Felder blieb der Speichern-Dialog aus, wenn
        // der Nutzer nur eine Zeilen-Auswahl / Bedingung am eingebundenen Sub-Modul
        // aendert — Aenderungen gingen dann beim Schliessen verloren.
        dataSheetId: s.dataSheetId ?? null,
        dataSheetFilters: s.dataSheetFilters ?? null,
        stepConditions: s.kind === 'MODULE_REF' ? stepConditionsOf(s) : null,
      })),
    });
  }
}

/** Vergleichsform eines Entwurfs: was beim Uebernehmen beim Schritt ankaeme, bei halber Bedingung samt Eingabe. */
function tdbSignature({ draft, sheetId, condition }: TdbDraftState): string {
  const half = missingConditionFields(draft).length > 0;
  return JSON.stringify({
    sheetId,
    filters: parseFilterExpression(draft.filter ?? '').entries,
    condition: condition ? Object.entries(condition).sort(([a], [b]) => a.localeCompare(b)) : null,
    half: half ? [draft.subj ?? '', draft.ref ?? '', draft.op ?? '', draft.val ?? ''] : null,
  });
}

function withoutFilter(override: TdbOverride): TdbOverride {
  return { ...override, filter: '' };
}

function hasHalfRow(rows: readonly TdbOverride[] | undefined): boolean {
  return !!rows?.some((row) => missingConditionFields(row).length > 0);
}

/** Vollstaendige Entwuerfe stehen schon im Schritt oder sind verworfen; nur halbe ueberdauern das Laden. */
function incompleteOnly(drafts: Record<string, TdbOverride[]>): Record<string, TdbOverride[]> {
  return Object.fromEntries(Object.entries(drafts).filter(([, rows]) => hasHalfRow(rows)));
}
